import React from "react";
import { inject, observer } from "mobx-react";
import { CalculateStore } from "../../../stores/calculateStore";
import NnwInsured from "./nnwInsured/nnwInsured";
import { ProposalStore } from "../../../stores/proposalStore";
import { UiFlagsStore } from "../../../stores/uiFlagsStore";
import { ErrorMessages } from "../../../helpers/errorMessage";
import { format } from "react-string-format";
import { PeselUtility } from "../../../helpers/peselUtility";

interface NnwClientsProps {
  calculateStore?: CalculateStore;
  proposalStore?: ProposalStore;
  uiFlagsStore?: UiFlagsStore;
}

@inject("calculateStore", "proposalStore", "uiFlagsStore")
@observer
export default class NnwClients extends React.Component<NnwClientsProps> {
  public render() {
    return (
      <>
        {this.numberOfNnwClients() > 0 && (
          <div className="nnw-clients">
            {this.renderErrorMessage()}
            {this.renderNnwInsureds()}
          </div>
        )}
      </>
    );
  }

  private renderNnwInsureds() {
    const container: JSX.Element[] = [];

    for (let i = 0, numberOfNnwClients = this.numberOfNnwClients(); i < numberOfNnwClients; i++) {
      const headerText = numberOfNnwClients === 1 ? `Ubezpieczony NNW` : `Ubezpieczony NNW (${i + 1})`;

      container.push(
        <NnwInsured
          key={`nnw-client-${i}`}
          client={this.props.proposalStore!.nnw.clients[i]}
          headerText={headerText} />
      );
    }

    return container;
  }

  private numberOfNnwClients() {
    const calculateStore = this.props.calculateStore!;

    return calculateStore.additionalInsurances.nnwType.checked ? Number(calculateStore.additionalInsurances.nnwType.numberOfClients) : 0;
  }

  private renderErrorMessage() {
    return (<div>
      {this.shouldRenderTooManySeniorsError() && this.renderTooManySeniorsError()}
      {this.shouldRenderNotEnoughSeniorsError() && this.renderNotEnoughSeniorsError()}
      {this.shouldRenderTooManyHeartAttackAndStrokeInsured() && this.renderTooManyHeartAttackAndStrokeInsuredError()}
      {this.shouldRenderNotEnoughHeartAttackAndStrokeInsured() && this.renderNotEnoughHeartAttackAndStrokeInsuredError()}
      {this.shouldRenderPeselDuplicationError() && this.renderPeselDuplicationError()}
    </div>);
  }

  private shouldRenderTooManyHeartAttackAndStrokeInsured() {
    const heartAttackAndStrokeChecked = this.props.calculateStore!.additionalInsurances.nnwHeartAttackAndStroke.checked;
    return heartAttackAndStrokeChecked && (this.getHeartAttackAndStrokeInsuredDifference() < 0);
  }

  private renderTooManyHeartAttackAndStrokeInsuredError() {
    return (
      <div className="error-message">
        {format(ErrorMessages.nnw.tooManyHeartAttackAndStrokeInsured, Math.abs(this.getHeartAttackAndStrokeInsuredDifference())).toString()}
      </div>
    );
  }

  private shouldRenderNotEnoughHeartAttackAndStrokeInsured() {
    if (this.isPeselsValidLength()) {
      const heartAttackAndStrokeChecked = this.props.calculateStore!.additionalInsurances.nnwHeartAttackAndStroke.checked;
      return heartAttackAndStrokeChecked && (this.getHeartAttackAndStrokeInsuredDifference() > 0);
    }
    return false;
  }

  private renderNotEnoughHeartAttackAndStrokeInsuredError() {
    return (
      <div className="error-message">
        {format(ErrorMessages.nnw.notEnoughHearAttackAndStrokeInsured, Math.abs(this.getHeartAttackAndStrokeInsuredDifference())).toString()}
      </div>
    );
  }

  private shouldRenderPeselDuplicationError() {
    return this.props.proposalStore!.nnw.nnwClientsDuplicate;
  }

  private shouldRenderTooManySeniorsError() {
    return this.getSafeSeniorsInsuredDifference() > 0;
  }

  private shouldRenderNotEnoughSeniorsError() {
    if (this.isPeselsValidLength()) {
      return this.getSafeSeniorsInsuredDifference() < 0;
    }
    return false;
  }

  private renderPeselDuplicationError() {
    return (
      <div className="error-message">
        Numery PESEL nie mogą się powtarzać
      </div>
    );
  }

  private renderTooManySeniorsError() {
    return (<div
      className="error-message">
      Wpisano więcej osób, które ukończyły 60 lat niż zadeklarowano dla ubezpieczenia NNW na&nbsp;
      <span
        onClick={() => {
          this.props.uiFlagsStore!.decrementStep();
        }}>
        poprzednim kroku
      </span>
    </div>);
  }

  private renderNotEnoughSeniorsError() {
    return (<div
      className="error-message">
      Wpisano mniej osób, które ukończyły 60 lat niż zadeklarowano dla ubezpieczenia NNW na&nbsp;
      <span
        onClick={() => {
          this.props.uiFlagsStore!.decrementStep();
        }}>
        poprzednim kroku
      </span>
    </div>);
  }

  private getHeartAttackAndStrokeInsuredDifference() {
    const proposalHeartAttackAndStrokeInsured = this.props.proposalStore!.nnw.clients
      .filter(client => client.heartAttackAndStroke)
      .length;
    const calculationHeartAttackAndStrokeInsured = Number(this.props.calculateStore!.additionalInsurances.nnwHeartAttackAndStroke.numberOfClients);
    const insuredDifference = calculationHeartAttackAndStrokeInsured - proposalHeartAttackAndStrokeInsured!;

    return insuredDifference;
  }

  private getSafeSeniorsInsuredDifference() {
    const numberOfSeniors = this.props.proposalStore!.nnw.numberOfSeniors;
    const nnwSenior = this.props.calculateStore!.additionalInsurances.nnwSenior;
    const declaredNumberOfSeniors = nnwSenior.checked ? Number(nnwSenior.nnwNumberOfSeniors) : 0;
    const insuredDifference = numberOfSeniors - declaredNumberOfSeniors;

    return insuredDifference;
  }

  private isPeselsValidLength() {
    const amountOfInCorrectPesels = this.props.proposalStore!.nnw.clients
      .filter(client => client.pesel.value.length !== PeselUtility.PESEL_LENGTH)
      .length;
    return amountOfInCorrectPesels === 0;
  }
}
